import { useAppSelector } from "@app/hooks"
import { RootState } from "@app/store"
import PendingDataPresenter from "@cmp/loaders/pendingDataPresenter"
import { ProgramDto } from "@masterschool/course-builder-api"
import PendingDataState from "@utils/pendingDataState"
import { useNavigate, useParams } from "react-router-dom"
import { QueryNotFoundPanel } from "@cmp/loaders/queryStatePanel"
import useFetchProgramData from "../../useFetchProgramData"
import { Box } from "@mui/material"
import ProgramView from "../layout/programView"
import ProgramSidebar from "@cmp/sidebar/programSidebar"
import appTheme from "../../../theme/appTheme"
import { createSelector } from "@reduxjs/toolkit"
import { LegacyProgramHeader, ProgramHeader } from "./programHeader"
import { isLegacyProgram } from "../program.helper"

function ProgramViewContainer() {
  const { programId } = useParams()
  useFetchProgramData(programId)
  const program = useAppSelector(selectProgram(programId))
  const navigate = useNavigate()

  if (program === "not-found") {
    return (
      <QueryNotFoundPanel
        title="We couldn't find this program."
        description="It may have been deleted, or the link may be wrong."
        backLabel="Back to programs"
        onBack={() => navigate("/programs")}
      />
    )
  }

  return (
    <PendingDataPresenter
      state={program}
      component={ProgramViewPresenter}
      loadedComponents={`program ${programId}`}
    />
  )
}

function ProgramViewPresenter(props: { data: ProgramDto }) {
  const isLegacy = isLegacyProgram(props.data)

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        bgcolor: isLegacy
          ? appTheme.palette.eTypes.sand15
          : appTheme.palette.primary.contrast,
      }}
    >
      {isLegacy ? (
        <LegacyProgramHeader program={props.data} />
      ) : (
        <ProgramHeader program={props.data} />
      )}
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        <ProgramSidebar isLegacy={isLegacy} />
        <ProgramView program={props.data} />
      </Box>
    </Box>
  )
}

const selectProgram = (programId?: string) =>
  createSelector(
    [
      (state: RootState) => state.program.programs,
      (state: RootState) => state.program.loadingState,
    ],
    (programs, loadingState): PendingDataState<ProgramDto> | "not-found" =>
      programState(programs, programId, loadingState),
  )

const programState = (
  programs: ProgramDto[],
  programId: string | undefined,
  loadingState: "idle" | "loading" | "loaded" | "failed",
): PendingDataState<ProgramDto> | "not-found" => {
  if (!programId) {
    return { status: "idle" }
  }
  const program = programs.find((p) => p.id === programId)
  if (program) {
    return { status: "success", data: program }
  }

  switch (loadingState) {
    case "idle":
      return {
        status: "idle",
      }
    case "loading":
      return { status: "pending" }
    // The list is in and this id isn't there: settle to not-found instead
    // of spinning forever.
    case "loaded":
      return "not-found"
    case "failed":
      return { status: "error" }
    default:
      return { status: "idle" }
  }
}

export default ProgramViewContainer
